import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { User } from 'firebase/auth';
import { ConversationService } from '../services/conversationService';
import { Conversation, Message } from '../types';
import {
  LocalGradeProcessor,
  ConversationGradeState,
  RawGrade,
} from '../services/localGradeProcessor';
import { useGradeSync } from './useGradeSync';
import {
  collection,
  addDoc,
  Timestamp,
  doc,
  getDoc,
  getDocs,
  increment,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { compareMajorityJudgments } from '../services/majorityJudgmentService';

/**
 * Calculate rankings from local grade state
 * Sorts models by majority judgment (best to worst)
 */
function calculateRankings(gradeState: ConversationGradeState | null): Array<{
  modelId: string;
  rank: number;
  medianGrade: string;
  gradeCount: number;
}> {
  if (!gradeState) return [];
  
  // Convert model grades from combined bucket to array (cross-priority merge for "this conversation")
  const modelArray = Array.from(gradeState.thisConversationCombined.modelGrades.entries()).map(([modelId, agg]) => ({
    modelId,
    majorityJudgment: agg.majorityJudgment
  }));
  
  // Sort by majority judgment (best to worst)
  // Use proper majority judgment comparison with tie-breaking
  modelArray.sort((a, b) => {
    return compareMajorityJudgments(a.majorityJudgment, b.majorityJudgment);
  });
  
  // Return ranked candidates with positions
  return modelArray.map((model, index) => ({
    modelId: model.modelId,
    rank: index + 1,
    medianGrade: model.majorityJudgment.medianGrade,
    gradeCount: model.majorityJudgment.judgmentsNumber ?? 0
  }));
}

/**
 * Call batch aggregation Cloud Function for user/global levels
 * This is computation-heavy and runs last, after all data is persisted
 */
async function callBatchAggregation(
  conversationId: string,
  userId: string
): Promise<void> {
  const functions = getFunctions();
  const batchAggregateGrades = httpsCallable(functions, 'batchAggregateGrades');

  const result = await batchAggregateGrades({
    conversationId,
    userId,
  });

  console.log('📊 Batch aggregation result:', result.data);
}

export const useConversation = (user: User | null) => {
  const [currentConversationId, setCurrentConversationId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [publishedConversations, setPublishedConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const conversationIdRef = useRef<string | null>(null);
  
  // Local grade processing state
  // Helper to create empty grade state
  const createEmptyGradeState = useCallback((conversationId: string): ConversationGradeState => ({
    conversationId,
    buckets: {
      free: { modelGrades: new Map(), creatorGrades: new Map() },
      speed: { modelGrades: new Map(), creatorGrades: new Map() },
      balance: { modelGrades: new Map(), creatorGrades: new Map() },
      value: { modelGrades: new Map(), creatorGrades: new Map() },
      intelligence: { modelGrades: new Map(), creatorGrades: new Map() },
      custom: { modelGrades: new Map(), creatorGrades: new Map() },
    },
    thisConversationCombined: {
      modelGrades: new Map(),
      creatorGrades: new Map(),
    },
    selfEvalGrades: {
      free: new Map(),
      speed: new Map(),
      balance: new Map(),
      value: new Map(),
      intelligence: new Map(),
      custom: new Map(),
    },
    selfEvalCombined: new Map(),
    rawGrades: [],
    lastUpdated: new Date(),
  }), []);
  
  const [localGradeState, setLocalGradeState] = useState<ConversationGradeState | null>(null);
  const [pendingConversationId, setPendingConversationId] = useState<string | null>(null);
  const gradeProcessor = useMemo(() => new LocalGradeProcessor(), []);
  
  // +Human feature state
  const [txtPaused, setTxtPaused] = useState(false);
  const [txtPausePhase, setTxtPausePhase] = useState<'awaiting-model-grades' | 'awaiting-ionoff-grade' | null>(null);
  const [txtModelGradesReceived, setTxtModelGradesReceived] = useState(0);
  const [txtIonoffGradeReceived, setTxtIonoffGradeReceived] = useState(0);
  const [txtResumeMessageId, setTxtResumeMessageId] = useState<string | null>(null);
  const [isResuming, setIsResuming] = useState(false); // Prevent duplicate resume calls
  
  // Create a stable serialization key for localGradeState to prevent infinite re-renders
  // Only changes when the actual content changes, not the object reference
  const localGradeStateKey = useMemo(() => {
    if (!localGradeState) return null;
    return JSON.stringify({
      conversationId: localGradeState.conversationId,
      bucketSizes: {
        free: localGradeState.buckets.free.modelGrades.size,
        speed: localGradeState.buckets.speed.modelGrades.size,
        balance: localGradeState.buckets.balance.modelGrades.size,
        value: localGradeState.buckets.value.modelGrades.size,
        intelligence: localGradeState.buckets.intelligence.modelGrades.size,
        custom: localGradeState.buckets.custom.modelGrades.size,
      },
      combinedSize: localGradeState.thisConversationCombined.modelGrades.size,
      selfEvalSizes: {
        free: localGradeState.selfEvalGrades.free.size,
        speed: localGradeState.selfEvalGrades.speed.size,
        balance: localGradeState.selfEvalGrades.balance.size,
        value: localGradeState.selfEvalGrades.value.size,
        intelligence: localGradeState.selfEvalGrades.intelligence.size,
        custom: localGradeState.selfEvalGrades.custom.size,
      },
      selfEvalCombinedSize: localGradeState.selfEvalCombined.size,
    });
  }, [localGradeState]);

  // Keep ref in sync with state
  useEffect(() => {
    conversationIdRef.current = currentConversationId;
  }, [currentConversationId]);

  // Subscribe to current conversation document for processing state
  useEffect(() => {
    if (!currentConversationId) {
      setIsProcessing(false);
      setTxtPaused(false);
      setTxtPausePhase(null);
      setTxtModelGradesReceived(0);
      setTxtIonoffGradeReceived(0);
      setTxtResumeMessageId(null);
      return;
    }

    const unsubscribe = ConversationService.subscribeToConversation(
      currentConversationId,
      (conversationData) => {
        setIsProcessing(conversationData?.processing === true);

        const paused = conversationData?.txtPaused === true;
        setTxtPaused(paused);

        const pausePhase = (conversationData?.txtPausePhase ?? null) as typeof txtPausePhase;
        const modelGradesReceived =
          typeof conversationData?.txtModelGradesReceived === 'number'
            ? conversationData.txtModelGradesReceived
            : 0;
        const ionoffGradesReceived =
          typeof conversationData?.txtIonoffGradeReceived === 'number'
            ? conversationData.txtIonoffGradeReceived
            : 0;

        if (paused) {
          setTxtPausePhase(pausePhase);
          setTxtModelGradesReceived(modelGradesReceived);
          setTxtIonoffGradeReceived(ionoffGradesReceived);

          const resumeMessageId =
            pausePhase === 'awaiting-ionoff-grade'
              ? (conversationData?.txtIonoffResumeMessageId as string | undefined)
              : (conversationData?.txtModelResumeMessageId as string | undefined);
          setTxtResumeMessageId(resumeMessageId ?? null);
        } else {
          setTxtPausePhase(null);
          setTxtModelGradesReceived(0);
          setTxtIonoffGradeReceived(0);
          setTxtResumeMessageId(null);
        }
      }
    );

    return () => unsubscribe();
  }, [currentConversationId]);

  // 🔍 CRITICAL: Subscribe to grades from Firestore to populate localGradeState
  // This ensures that even if grades are written by the backend (e.g., during tests),
  // the frontend picks them up and can trigger synthesis
  // DISABLED: This Firestore grades collection listener conflicts with useGradeSync
  // useGradeSync already extracts grades from messages' rawGradingData field (both peer and self-eval)
  // The Firestore grades collection only contains peer grades, not self-eval grades
  // When this listener fires, it overwrites the correct state from useGradeSync
  // This listener might be legacy code or only needed for star grades (manual grades)
  // For now, we rely exclusively on useGradeSync for automatic grade extraction
  
  // useEffect(() => {
  //   if (!currentConversationId || !user) {
  //     setLocalGradeState(null);
  //     return;
  //   }

  //   console.log('🔍 Setting up grades listener for conversation:', currentConversationId);

  //   const unsubscribe = onSnapshot(
  //     query(
  //       collection(db, 'grades'),
  //       where('conversationId', '==', currentConversationId)
  //     ),
  //     async (snapshot) => {
  //       if (snapshot.empty) {
  //         console.log('📭 No grades found in Firestore for this conversation');
  //         return;
  //       }

  //       console.log(`📬 Received ${snapshot.size} grades from Firestore`);

  //       const grades: RawGrade[] = [];
  //       snapshot.forEach(doc => {
  //         const data = doc.data();
  //         grades.push({
  //           graderModelId: data.graderModelId,
  //           candidateModelId: data.candidateModelId,
  //           userPromptMessageId: data.userPromptMessageId,
  //           grade: data.grade,
  //           isSelfEval: data.isSelfEval || false,
  //           conversationId: data.conversationId || currentConversationId,
  //           timestamp: data.timestamp?.toDate?.() || new Date(),
  //           ionoffEquals: data.ionoffEquals,
  //           ionoffVersion: data.ionoffVersion,
  //           synthesisPromptVariant: data.synthesisPromptVariant,
  //           synthesisPromptVersion: data.synthesisPromptVersion,
  //           prioritySetting: data.prioritySetting,
  //           reasoningSetting: data.reasoningSetting,
  //           responseWordCount: data.responseWordCount,
  //           selfevalPrehocSetting: data.selfevalPrehocSetting,
  //           webGroundingSetting: data.webGroundingSetting,
  //           fallbackGrader: data.fallbackGrader || false,
  //         });
  //       });

  //       try {
  //         // Process grades into local state
  //         const newState = await gradeProcessor.processConversation(currentConversationId, grades, messages);
  //         setLocalGradeState(newState);
  //         console.log(`✅ Processed ${grades.length} grades into localGradeState`, {
  //           peerGrades: grades.filter(g => !g.isSelfEval).length,
  //           selfEvalGrades: grades.filter(g => g.isSelfEval).length,
  //         });
  //       } catch (error) {
  //         console.error('❌ Failed to process grades from Firestore:', error);
  //       }
  //     },
  //     (error) => {
  //       console.error('❌ Error listening to grades:', error);
  //     }
  //   );

  //   return () => unsubscribe();
  // }, [currentConversationId, user, messages, gradeProcessor]);

  // Timeout mechanism: auto-clear processing flag if hung for too long
  // This prevents the stop button from being stuck indefinitely
  useEffect(() => {
    if (!isProcessing || !currentConversationId) return;
    
    // Set timeout for 5 minutes (generous for slow models and large batches)
    const timeoutId = setTimeout(async () => {
      console.warn('⚠️ Processing timeout detected (5 minutes), auto-clearing flag');
      await ConversationService.clearProcessingFlag(currentConversationId);
    }, 5 * 60 * 1000); // 5 minutes
    
    return () => clearTimeout(timeoutId);
  }, [isProcessing, currentConversationId]);

  // Subscribe to user's conversations list
  useEffect(() => {
    if (!user) {
      setConversations([]);
      return;
    }

    const unsubscribe = ConversationService.subscribeToConversations(
      user.uid,
      (newConversations) => {
        // console.log('🔔 Received conversations from Firestore:', newConversations);
        setConversations(newConversations);
      }
    );

    return () => unsubscribe();
  }, [user]);

  // Subscribe to published conversations (real-time listener)
  // This automatically loads published conversations including batch tests
  useEffect(() => {
    if (!user) return;

    console.log('👁️ Setting up published conversation listener');
    
    // Query for published conversations (includes batch tests with published=true)
    // Fetch 50 to allow "show all" toggle (frontend slices to 10 by default)
    // Sort by createdAt to prevent republish gaming
    // Filter by active=true so reset-to-scratch.js properly clears them
    const publishedQuery = query(
      collection(db, 'conversations'),
      where('published', '==', true),
      where('active', '==', true),
      orderBy('createdAt', 'desc'),
      limit(50) // Fetch 50, frontend shows 10 by default (or all via toggle)
    );

    const unsubscribe = onSnapshot(
      publishedQuery,
      (snapshot) => {
        const publishedConversations = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate() || new Date(),
          lastUpdatedAt: doc.data().lastUpdatedAt?.toDate() || new Date()
        })) as Conversation[];

        if (publishedConversations.length > 0) {
          console.log(`📢 Received ${publishedConversations.length} published conversations`);
        }
        
        // Store published conversations separately (don't merge with user's conversations)
        setPublishedConversations(publishedConversations);
      },
      (error) => {
        console.error('❌ Error in published conversation listener:', error);
      }
    );

    return () => {
      console.log('🔌 Unsubscribing from published conversation listener');
      unsubscribe();
    };
  }, [user]);

  // Keep messages in a ref for stable access in callbacks
  const messagesRef = useRef<Message[]>([]);
  
  // Track if we're in the process of creating a new conversation to prevent race conditions
  const isCreatingNewConversation = useRef(false);

  // Subscribe to messages in the current conversation
  useEffect(() => {
    if (!currentConversationId) {
      setMessages([]);
      messagesRef.current = [];
      setLocalGradeState(null); // Clear local grade state
      return;
    }

    console.log('📨 Subscribing to messages for conversation:', currentConversationId);

    const unsubscribe = ConversationService.subscribeToMessages(
      currentConversationId,
      (newMessages) => {
        console.log('📥 Received messages from Firestore:', {
          conversationId: currentConversationId,
          messageCount: newMessages.length,
          isCreatingNew: isCreatingNewConversation.current
        });
        
        // DEFENSIVE: Don't overwrite with old conversation's messages if we just created a new one
        if (isCreatingNewConversation.current && newMessages.length > 0) {
          console.warn('⚠️ Ignoring messages during new conversation creation');
          return;
        }
        
        setMessages(newMessages);
        messagesRef.current = newMessages; // Keep ref in sync

        // NOTE: We DON'T recompute localGradeState here anymore because:
        // 1. During real-time operation, grades are added by addGradesLocally which already has correct messages
        // 2. On refresh/navigation, useGradeSync processes all grades from Firestore with current messages
        // 3. Recomputing here causes race conditions where we overwrite state being built by addGradesLocally
        // 
        // The message timestamps in grades are only used for tie-breaking in majority judgment,
        // and those are already correct when grades are initially processed.
      }
    );

    return () => {
      console.log('🔌 Unsubscribing from conversation:', currentConversationId);
      unsubscribe();
    };
  }, [currentConversationId, gradeProcessor]);

  // Process grades locally
  const processGradesLocally = useCallback((grades: RawGrade[]) => {
    if (!currentConversationId) return;
    
    console.log('🔍 processGradesLocally called', {
      conversationId: currentConversationId.substring(0, 8),
      gradesReceived: grades.length,
      peerGrades: grades.filter(g => !g.isSelfEval).length,
      selfEvalGrades: grades.filter(g => g.isSelfEval).length
    });
    
    // processConversation is now async (to fetch gradeRankings if needed)
    // getGradeRankings() returns immediately from cache, so this should be fast
    // CRITICAL: Pass isInitialLoad=true to apply filterToMostRecentRun and remove progressive grading artifacts
    gradeProcessor.processConversation(currentConversationId, grades, messagesRef.current, undefined, true).then((newState) => {
      console.log('🔍 useConversation: Setting localGradeState (processGradesLocally)', {
        conversationId: currentConversationId.substring(0, 8),
        selfEvalCombinedSize: newState.selfEvalCombined.size,
        combinedSize: newState.thisConversationCombined.modelGrades.size,
        rawGradesCount: newState.rawGrades.length,
        messagesCount: messagesRef.current.length
      });
      setLocalGradeState(newState);
    }).catch((error) => {
      console.error('❌ Local grade processing failed:', error);
    });
  }, [gradeProcessor, currentConversationId]);
  
  // Track grade IDs that we've already added to prevent rapid-fire duplicates
  const processedGradeIdsRef = useRef<Set<string>>(new Set());
  
  // Track latest state in ref to avoid stale closures without causing flicker
  const localGradeStateRef = useRef(localGradeState);
  useEffect(() => {
    localGradeStateRef.current = localGradeState;
  }, [localGradeState]);
  
  // CRITICAL: Queue for serializing grade additions to prevent race conditions
  // When multiple grade batches arrive simultaneously, they must process sequentially  
  // The queue passes state through the chain to avoid ref race conditions
  const gradeAdditionQueueRef = useRef<Promise<ConversationGradeState | null>>(Promise.resolve(null));
  
  // Add grades incrementally (SERIALIZED to prevent race conditions)
  const addGradesLocally = useCallback((newGrades: RawGrade[]) => {
    // CRITICAL: Deduplicate at point of entry to prevent async race conditions
    const trulyNewGrades = newGrades.filter(g => {
      const gradeKey = g.gradeId || `${g.graderModelId}|${g.candidateModelId}|${g.grade}|${g.userPromptMessageId}|${g.isSelfEval ? 'SE' : 'P'}`;
      if (processedGradeIdsRef.current.has(gradeKey)) {
        console.log('🚫 Blocked duplicate grade at entry point:', {
          grader: g.graderModelId.substring(0, 20),
          candidate: g.candidateModelId.substring(0, 20),
          grade: g.grade
        });
        return false;
      }
      processedGradeIdsRef.current.add(gradeKey);
      return true;
    });
    
    if (trulyNewGrades.length === 0) {
      console.log('⏭️ No new grades to add (all duplicates filtered at entry)');
      return;
    }
    
    console.log('✅ Adding grades locally (queuing):', {
      totalReceived: newGrades.length,
      trulyNew: trulyNewGrades.length,
      blocked: newGrades.length - trulyNewGrades.length
    });
    
    // CRITICAL: Chain this addition to the queue to prevent race conditions
    // Multiple grade batches arriving simultaneously must process sequentially
    // State is passed through the promise chain to avoid ref race conditions
    gradeAdditionQueueRef.current = gradeAdditionQueueRef.current.then(async (previousState) => {
      // Use state from previous promise in chain, or fall back to ref
      const currentState = previousState || localGradeStateRef.current;
      
      if (!currentState) {
        console.error('❌ No localGradeState available - skipping grade addition');
        return null;
      }
      
      console.log('🔄 Processing grade batch from queue:', {
        gradesInBatch: trulyNewGrades.length,
        currentRawGradesCount: currentState.rawGrades.length,
        usingPreviousState: !!previousState
      });
      
      const updatedState = await gradeProcessor.addGrades(currentState, trulyNewGrades, messagesRef.current);
      
        const peerGrades = updatedState.rawGrades.filter((g: RawGrade) => !g.isSelfEval);
        const selfEvalGrades = updatedState.rawGrades.filter((g: RawGrade) => g.isSelfEval);
        
        if (trulyNewGrades.some(g => !g.isSelfEval) && peerGrades.length > 0) {
          console.log('📊 Peer grades updated', {count: peerGrades.length});
        }
        
        if (trulyNewGrades.some(g => g.isSelfEval) && selfEvalGrades.length > 0) {
          console.log('📊 Self-eval grades updated', {count: selfEvalGrades.length});
        }
        
      console.log('🔍 useConversation: Setting updatedState from addGradesLocally', {
          selfEvalCombinedSize: updatedState.selfEvalCombined.size,
          combinedSize: updatedState.thisConversationCombined.modelGrades.size,
        creatorGradesSize: updatedState.thisConversationCombined.creatorGrades.size,
        rawGradesCount: updatedState.rawGrades.length,
        timestamp: updatedState.lastUpdated?.getTime(),
        creators: Array.from(updatedState.thisConversationCombined.creatorGrades.keys())
        });
        
      // Update both ref and React state
      localGradeStateRef.current = updatedState;
        setLocalGradeState(updatedState);
      
      // CRITICAL: Return state for next item in queue
      return updatedState;
      }).catch((error) => {
      console.error('❌ Grade addition queue error:', error);
      return null; // Return null to continue chain
    });
  }, [gradeProcessor]);

  // Sync grades from Firestore to local state
  useGradeSync({
    conversationId: currentConversationId,
    onGradesReceived: processGradesLocally,
    onNewGrades: addGradesLocally,
  });

  // Write grades to Firestore once after synthesis (and self-eval if enabled)
  // CRITICAL: Track by gradeId to handle multi-prompt conversations correctly
  const gradesWrittenRef = useRef<Set<string>>(new Set());
  
  // Track which prompts we've checked in Firestore to avoid redundant checks
  const firestoreCheckedPromptsRef = useRef<Set<string>>(new Set());
  
  useEffect(() => {
    // Only proceed if we have a conversation and local grade state
    if (!currentConversationId || !localGradeState) {
      return;
    }

    // Check if synthesis message exists (final ionoff response)
    const hasSynthesis = messages.some(m => m.isSynthesized);
    if (!hasSynthesis) {
      return;
    }

    // Separate peer and self-eval grades
    const peerGrades = localGradeState.rawGrades.filter(g => !g.isSelfEval);
    const selfEvalGrades = localGradeState.rawGrades.filter(g => g.isSelfEval);
    
    // Check if self-eval matrix exists (indicates self-eval is complete)
    const hasSelfEvalMatrix = messages.some(m => m.isGradeMatrix && m.content?.includes('Self-Eval'));
    
    // Get unique prompt IDs from grades to check per-prompt
    const peerPromptIds = Array.from(new Set(peerGrades.map(g => g.userPromptMessageId)));
    const selfEvalPromptIds = Array.from(new Set(selfEvalGrades.map(g => g.userPromptMessageId)));
    
    // CRITICAL: Check Firestore for grades BY PROMPT (not by conversation)
    // This allows P2 grades to be written even if P1 grades already exist
    const uncheckedPeerPrompts = peerPromptIds.filter(
      promptId => !firestoreCheckedPromptsRef.current.has(`${promptId}-peer`)
    );
    const uncheckedSelfEvalPrompts = selfEvalPromptIds.filter(
      promptId => !firestoreCheckedPromptsRef.current.has(`${promptId}-selfeval`)
    );
    
    // Check Firestore for each unchecked prompt
    if (uncheckedPeerPrompts.length > 0 || uncheckedSelfEvalPrompts.length > 0) {
      (async () => {
        try {
          // Mark all prompts as checked to prevent re-checking
          uncheckedPeerPrompts.forEach(p => firestoreCheckedPromptsRef.current.add(`${p}-peer`));
          uncheckedSelfEvalPrompts.forEach(p => firestoreCheckedPromptsRef.current.add(`${p}-selfeval`));
          
          // Query for existing grades in this conversation
          const gradesQuery = query(
            collection(db, 'grades'),
            where('conversationId', '==', currentConversationId)
          );
          const snapshot = await getDocs(gradesQuery);
          
          if (!snapshot.empty) {
            // Mark existing grades as written (by gradeId)
            snapshot.forEach(doc => {
              const data = doc.data();
              // Create gradeId from existing data for deduplication
              const existingGradeId = data.gradeId || 
                `${data.graderModelId}|${data.candidateModelId}|${data.userPromptMessageId}|${data.isSelfEval ? 'SE' : 'P'}`;
              gradesWrittenRef.current.add(existingGradeId);
            });
            console.log(`✅ Found ${snapshot.size} existing grades in Firestore - marked as written`);
          }
        } catch (error) {
          console.error('❌ Error checking for existing grades:', error);
        }
      })();
    }
    
    // Filter to only grades that haven't been written yet (by gradeId)
    const unwrittenPeerGrades = peerGrades.filter(g => {
      const gradeId = g.gradeId || 
        `${g.graderModelId}|${g.candidateModelId}|${g.userPromptMessageId}|P`;
      return !gradesWrittenRef.current.has(gradeId);
    });
    
    const unwrittenSelfEvalGrades = selfEvalGrades.filter(g => {
      const gradeId = g.gradeId || 
        `${g.graderModelId}|${g.candidateModelId}|${g.userPromptMessageId}|SE`;
      return !gradesWrittenRef.current.has(gradeId);
    });
    
    let gradesToWrite: typeof peerGrades = [];
    let writePhase = '';
    
    // Phase 1: Write unwritten peer grades after synthesis
    if (unwrittenPeerGrades.length > 0) {
      gradesToWrite = unwrittenPeerGrades;
      writePhase = 'peer';
      console.log(`📝 Phase 1: Writing ${unwrittenPeerGrades.length} NEW peer grades to Firestore...`);
    }
    // Phase 2: Write unwritten self-eval grades after self-eval matrix appears
    else if (unwrittenSelfEvalGrades.length > 0 && hasSelfEvalMatrix) {
      gradesToWrite = unwrittenSelfEvalGrades;
      writePhase = 'selfeval';
      console.log(`📝 Phase 2: Writing ${unwrittenSelfEvalGrades.length} NEW self-eval grades to Firestore...`);
    }
    // Nothing to write
    else {
      return;
    }

    const baseGrades = gradesToWrite;
    
    // Mark these specific grades as written immediately to prevent duplicate writes
    baseGrades.forEach(g => {
      const gradeId = g.gradeId || 
        `${g.graderModelId}|${g.candidateModelId}|${g.userPromptMessageId}|${g.isSelfEval ? 'SE' : 'P'}`;
      gradesWrittenRef.current.add(gradeId);
    });

      // Write grades directly to Firestore (async, don't block UI)
      (async () => {
      // NOTE: Duplication is already handled by localGradeProcessor.duplicateSelfEvalGradesForIonoffVersion()
      // in processConversation(), so localGradeState.rawGrades already includes duplicates.
      // We should NOT duplicate again here, or we'll get double/triple duplicates.
      const allGrades = baseGrades;
      
      // Count duplicates that were already created by localGradeProcessor
      // (these have candidateModelId starting with 'ionoff-grading-')
      const alreadyDuplicatedCount = allGrades.filter(g => 
        g.isSelfEval && g.candidateModelId.startsWith('ionoff-grading-')
      ).length;
      
      console.log('📝 Writing self-eval grades to Firestore', {
        totalGrades: allGrades.length,
        baseGrades: baseGrades.length,
        alreadyDuplicated: alreadyDuplicatedCount,
        note: 'Duplicates already created by localGradeProcessor'
      });
      
      let successCount = 0;
      let errorCount = 0;

      const writePromises = allGrades.map(async (grade) => {
        try {
          const gradeData: any = {
            grade: grade.grade,
            timestamp: Timestamp.fromDate(grade.timestamp),
            conversationId: grade.conversationId,
            userId: user?.uid, // CRITICAL: Use authenticated user ID for Firestore security rules
            modelId: grade.candidateModelId, // Legacy field name (for backward compatibility)
            candidateModelId: grade.candidateModelId, // Consistent field name
            creator: grade.creator,
            messageId: grade.gradeId,
            graderModelId: grade.graderModelId,
            userPromptMessageId: grade.userPromptMessageId,
            prioritySetting: grade.prioritySetting,
            active: true,
            isRetest: grade.isRetest || false,
            isSelfEval: grade.isSelfEval || false,
            
            // Metadata fields (omit undefined values for Firestore)
            aisNumber: grade.aisNumber,
            gradingPromptVersion: grade.gradingPromptVersion,
            promptWordCount: grade.promptWordCount,
            reasoningSetting: grade.reasoningSetting,
            responseWordCount: grade.responseWordCount,
            selfevalPrehocSetting: grade.selfevalPrehocSetting,
            webGroundingSetting: grade.webGroundingSetting,
            fallbackGrader: grade.fallbackGrader || false,
          };
          
          // Only add optional fields if they're defined
          if (grade.ionoffVersion !== undefined) {
            gradeData.ionoffVersion = grade.ionoffVersion;
          }
          if (grade.synthesisPromptVariant !== undefined) {
            gradeData.synthesisPromptVariant = grade.synthesisPromptVariant;
          }
          if (grade.synthesisPromptVersion !== undefined) {
            gradeData.synthesisPromptVersion = grade.synthesisPromptVersion;
          }

          // Use deterministic grade ID for idempotent writes
          if (!grade.gradeId) {
            console.error('❌ Grade missing gradeId:', grade);
            errorCount++;
            return;
          }
          
          // setDoc with merge:true allows idempotent writes (same ID = same grade, no duplicates)
          await setDoc(doc(db, 'grades', grade.gradeId), gradeData, { merge: true });
          successCount++;
        } catch (error) {
          errorCount++;
          console.error(`❌ Failed to write grade:`, error);
        }
      });

      await Promise.allSettled(writePromises);
      
      if (errorCount === 0) {
        console.log(`✅ Successfully wrote ${successCount} ${writePhase} grades to Firestore`);
      } else {
        console.error(`⚠️ Wrote ${successCount}/${allGrades.length} ${writePhase} grades (${errorCount} errors)`);
      }

      // Only run aggregation after the FINAL phase
      // If self-eval grades exist, wait for phase 2; otherwise run after phase 1
      const isFinalPhase = writePhase === 'selfeval' || (writePhase === 'peer' && selfEvalGrades.length === 0);
      
      if (isFinalPhase) {
        // FINAL PHASE: Only aggregate to user/global via Cloud Function.
        // Conversation-level aggregates are computed frontend-first from messages/localGradeState
        // and are NOT persisted back to Firestore to avoid double-counting on rehydration.
        // Step 2: Call batch aggregation for user/global levels (computation-heavy, last)
        try {
          console.log('🔄 Triggering batch aggregation for user/global levels...');
          await callBatchAggregation(currentConversationId, user?.uid || '');
          console.log('✅ Batch aggregation complete');
        } catch (error) {
          console.error('❌ Batch aggregation failed:', error);
        } finally {
          // Step 4: Clear processing flag from frontend (ensures stop button becomes send button)
          // This is a safety measure - backend should also clear it, but this ensures reliability
          try {
            await ConversationService.clearProcessingFlag(currentConversationId);
          } catch (error) {
            console.error('❌ Failed to clear processing flag:', error);
          }
        }
      } else {
        console.log(`⏭️ Skipping aggregation after ${writePhase} phase (waiting for self-eval)`);
      }
    })().catch(error => {
      console.error('❌ Grade writing failed:', error);
      gradesWrittenRef.current.delete(`${currentConversationId}-${writePhase}`);
    });
  }, [currentConversationId, messages, localGradeState, user, addGradesLocally]);

  // TWO-PHASE FLOW: Trigger synthesis when all peer grades received
  // 🎯 FIX: Track synthesis state PER user prompt message, not just the latest one
  // This allows concurrent prompts to each complete their synthesis independently
  const synthesisTriggeredRef = useRef<Set<string>>(new Set());
  const synthesisInProgressRef = useRef<Set<string>>(new Set());
  
  // Helper: Check if a message is an AI model response (not debug/grading/etc)
  const isModelResponse = useCallback((m: Message, userMessage: Message): boolean => {
    if (m.role !== 'assistant') return false;
    if (!m.modelUsed) return false;
    
    // Check userPromptMessageId if available
    if (m.userPromptMessageId) {
      if (m.userPromptMessageId !== userMessage.id) return false;
    }
    
    // Timestamp sanity check
    if (m.timestamp < userMessage.timestamp) return false;
    
    // Exclude human responses (they don't participate in peer grading)
    if (m.isHumanResponse) return false;
    
    // Exclude all special message types
    if (m.isSynthesized) return false;
    if (m.synthesisVariant) return false;
    if (m.isGradingResponse) return false;
    if (m.isExtractionResponse) return false;
    if (m.isCostSummary) return false;
    if (m.isGradeMatrix) return false;
    if (m.isDebugInfo) return false;
    if ((m as any).isShannonDebug) return false; // Shannon surprise debug messages
    if ((m as any).isPromptAnalysisDebug) return false; // Prompt analysis debug messages
    
    // Exclude debug/system messages by content
    const content = m.content || '';
    if (content.includes('Title Generation')) return false;
    if (content.includes('API Call')) return false;
    if (content.includes('Grading API Call')) return false;
    if (content.includes('Collabograde')) return false;
    if (content.includes('Grade Extraction')) return false;
    if (content.includes('Shannon Surprise')) return false;
    if (content.includes('Prompt Analysis')) return false;
    if (content.includes('Retry Debug')) return false;
    if (content.includes('Fallback Loop')) return false;
    if (content.includes('Fallback Round')) return false;
    if (content.includes('Fallback Model')) return false;
    if (content.includes('Fallback Succeeded')) return false;
    if (content.includes('Fallback Failed')) return false;
    if (content.includes('candidate1_grade') && content.includes('candidate2_grade')) return false;
    
    return true;
  }, []);
  
  // Helper: Calculate expected peer grades for a user message
  const calculateExpectedGrades = useCallback((userMessage: Message, allMessages: Message[]): number => {
    const aiResponses = allMessages.filter(m => isModelResponse(m, userMessage));
    const N = aiResponses.length;
    if (N === 0) return 0;
    
    const txtEnabled = userMessage?.sendConfig?.txt ?? false;
    // Use the selfGradeEnabled setting from when this prompt was sent
    const selfGradeEnabled = userMessage?.sendConfig?.selfGradeEnabled ?? false;
    
    if (txtEnabled) {
      // TxT mode: Each model grades all others (+ self if enabled)
      // Without self-grading: N × (N-1) grades
      // With self-grading: N × N grades
      return selfGradeEnabled ? N * N : N * (N - 1);
    } else {
      // Progressive mode: Calculate expected grades using triangle formula
      // Base formula without self-grading: 2N, 2N+2, or 2N+4
      // With self-grading: add N (one self-grade per grader) → 3N, 3N+2, or 3N+4
      const baseMultiplier = selfGradeEnabled ? 3 : 2;
      const remainder = N % 3;
      if (remainder === 0) return baseMultiplier * N;
      if (remainder === 1) return baseMultiplier * N + 4;
      return baseMultiplier * N + 2;
    }
  }, [isModelResponse]);
  
  // 🎯 NEW: Track ALL pending user prompts that need synthesis
  // Returns array of user messages that have all grades but no synthesis yet
  const pendingPromptsForSynthesis = useMemo(() => {
    if (!currentConversationId || !messages.length || !localGradeState) return [];
    
    const userMessages = messages.filter(m => m.role === 'user');
    const pendingPrompts: Message[] = [];
    
    for (const userMsg of userMessages) {
      const synthesisKey = `${currentConversationId}-${userMsg.id}-synthesis`;
      
      // Skip if already triggered or in progress
      if (synthesisTriggeredRef.current.has(synthesisKey)) continue;
      if (synthesisInProgressRef.current.has(synthesisKey)) continue;
      
      // Check if synthesis already exists for this prompt
      const hasSynthesis = messages.some(m => 
        m.isSynthesized && 
        !m.synthesisVariant &&
        m.userPromptMessageId === userMsg.id
      );
      if (hasSynthesis) {
        synthesisTriggeredRef.current.add(synthesisKey);
        continue;
      }
      
      // Calculate expected grades for THIS prompt
      const expectedGrades = calculateExpectedGrades(userMsg, messages);
      if (expectedGrades === 0) continue;
      
      // Count grades received for THIS prompt (by userPromptMessageId)
      const gradesForPrompt = localGradeState.rawGrades.filter(g => 
        !g.isSelfEval && g.userPromptMessageId === userMsg.id
      );
      
      if (gradesForPrompt.length >= expectedGrades) {
        console.log('🎯 Prompt ready for synthesis:', {
          userPromptId: userMsg.id,
          expectedGrades,
          receivedGrades: gradesForPrompt.length,
          content: userMsg.content.substring(0, 50)
        });
        pendingPrompts.push(userMsg);
      }
    }
    
    return pendingPrompts;
  }, [currentConversationId, messages, localGradeState, calculateExpectedGrades]);

  // Legacy: Keep these for backward compatibility with other components
  const expectedPeerGrades = useMemo(() => {
    if (!currentConversationId || !messages.length) return 0;
    const userMessages = messages.filter(m => m.role === 'user');
    if (userMessages.length === 0) return 0;
    const lastUserMessage = userMessages[userMessages.length - 1];
    return calculateExpectedGrades(lastUserMessage, messages);
  }, [currentConversationId, messages, calculateExpectedGrades]);

  const allPeerGradesReceived = useMemo(() => {
    return pendingPromptsForSynthesis.length > 0;
  }, [pendingPromptsForSynthesis]);

  const lastUserMessage = useMemo(() => {
    const userMessages = messages.filter(m => m.role === 'user');
    if (userMessages.length === 0) return undefined;
    return userMessages.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())[0];
  }, [messages.filter(m => m.role === 'user').map(m => m.id).join(',')]);

  // 🎯 NEW: Trigger synthesis for EACH pending prompt independently
  useEffect(() => {
    if (!currentConversationId || !user || pendingPromptsForSynthesis.length === 0) {
      return;
    }
    
    // Process each pending prompt
    for (const targetUserMessage of pendingPromptsForSynthesis) {
      const synthesisKey = `${currentConversationId}-${targetUserMessage.id}-synthesis`;
      
      // Double-check refs (may have changed since memo computed)
      if (synthesisTriggeredRef.current.has(synthesisKey)) continue;
      if (synthesisInProgressRef.current.has(synthesisKey)) continue;
      
      // Mark as triggered AND in-progress BEFORE async work
      synthesisTriggeredRef.current.add(synthesisKey);
      synthesisInProgressRef.current.add(synthesisKey);
      
      console.log('🚀 Triggering synthesis for prompt:', {
        userPromptId: targetUserMessage.id,
        content: targetUserMessage.content.substring(0, 50),
        synthesisKey
      });
    
      // 🎯 Use IIFE with captured targetUserMessage to avoid closure issues
      const triggerForPrompt = async (promptMessage: Message, key: string) => {
        try {
          console.log('🎯 Triggering synthesis for:', promptMessage.id);
          
          // Calculate rankings from local grade state for THIS prompt
          // Filter grades to only those for this specific prompt
          const promptGrades = {
            ...localGradeState!,
            conversationId: currentConversationId!, // Ensure required field
            rawGrades: localGradeState!.rawGrades.filter(g => 
              g.userPromptMessageId === promptMessage.id
            )
          };
          const rankedCandidates = calculateRankings(promptGrades);
          console.log('📊 Ranked candidates for prompt:', rankedCandidates);
          
          // Get ALL responses for THIS specific prompt
          const promptMessageTime = promptMessage.timestamp;
          const allResponses = messages
            .filter(m => {
              if (m.role !== 'assistant') return false;
              if (!m.modelUsed) return false;
              
              // 🎯 Must match THIS prompt's ID
              if (m.userPromptMessageId) {
                if (m.userPromptMessageId !== promptMessage.id) return false;
              }
              
              if (m.timestamp < promptMessageTime) return false;
              if (m.isPromptResponse !== true) return false;
              if (m.isSynthesized) return false;
              if (m.synthesisVariant) return false;
              if (m.isGradingResponse) return false;
              if (m.isExtractionResponse) return false;
              if (m.isCostSummary) return false;
              if (m.isGradeMatrix) return false;
              if (m.isDebugInfo) return false;
              if ((m as any).isShannonDebug) return false;
              if ((m as any).isPromptAnalysisDebug) return false;
              
              return true;
            })
            .map(m => {
              let actualModelId = m.actualModelId;
              if (!actualModelId && m.modelUsed) {
                actualModelId = m.modelUsed.replace(/\s*\((high|low|medium|default)\)\s*$/, '');
              }
              
              return {
                modelId: actualModelId || m.modelUsed,
                displayedModelId: m.modelUsed,
                content: m.content,
                costUSD: m.costUSD || 0,
                isHumanResponse: m.isHumanResponse || false,
              };
            });
          
          // Sort responses by rankings
          console.log('🔍 Debug: Matching rankedCandidates to responses', {
            rankedCandidates: rankedCandidates.map(r => ({ modelId: r.modelId, rank: r.rank })),
            allResponses: allResponses.map(r => ({ displayedModelId: r.displayedModelId })),
          });
          const rankedResponses = allResponses.sort((a, b) => {
            const matchA = rankedCandidates.find(r => r.modelId === a.displayedModelId);
            const matchB = rankedCandidates.find(r => r.modelId === b.displayedModelId);
            const rankA = matchA?.rank || 999;
            const rankB = matchB?.rank || 999;
            if (!matchA) {
              console.warn('⚠️ No rank match for response:', {
                displayedModelId: a.displayedModelId,
                availableModelIds: rankedCandidates.map(r => r.modelId),
              });
            }
            if (!matchB) {
              console.warn('⚠️ No rank match for response:', {
                displayedModelId: b.displayedModelId,
                availableModelIds: rankedCandidates.map(r => r.modelId),
              });
            }
            return rankA - rankB;
          });
          console.log('✅ Sorted responses order:', rankedResponses.map(r => ({
            displayedModelId: r.displayedModelId,
            rank: rankedCandidates.find(rc => rc.modelId === r.displayedModelId)?.rank || 999,
          })));
          
          const aiResponses = rankedResponses.filter(r => !r.isHumanResponse);
          
          // Build conversation history (all messages before THIS prompt)
          const conversationHistory = messages
            .filter(m => 
              m.timestamp < promptMessageTime &&
              (
                m.role === 'user' || 
                (m.role === 'assistant' && m.isSynthesized === true)
              ) &&
              !m.isGradingResponse &&
              !m.isDebugInfo &&
              !m.isGradeMatrix
            )
            .map(m => ({
              role: m.role,
              content: m.content,
            }));
          
          // Extract settings from THIS prompt's sendConfig
          const selfEvalEnabled = promptMessage.sendConfig?.selfeval ?? false;
          const webEnabled = promptMessage.sendConfig?.webGrounding ?? true;
          const reasoningEffort = promptMessage.sendConfig?.reasoning || 'default';
          const txtEnabled = promptMessage.sendConfig?.txt ?? false;
          const plusHumanEnabled = promptMessage.sendConfig?.plusHumanEnabled ?? false;
          const prioritize = promptMessage.sendConfig?.prioritize || 'balance';
          
          console.log('📦 Synthesis payload for prompt:', {
            promptId: promptMessage.id,
            aiResponsesCount: rankedResponses.length,
            conversationHistoryCount: conversationHistory.length,
            userPrompt: promptMessage.content.substring(0, 50),
            selfEvalEnabled,
            webEnabled,
            reasoningEffort,
            txtEnabled,
            plusHumanEnabled,
            prioritize,
          });
          
          const functions = getFunctions();
          const triggerSynthesisFn = httpsCallable(functions, 'triggerSynthesis');
          
          const responsesForSynthesis = txtEnabled 
            ? rankedResponses
            : rankedResponses.filter(r => !r.isHumanResponse);
          
          const result = await triggerSynthesisFn({
            conversationId: currentConversationId,
            userId: user!.uid,
            aiResponses: responsesForSynthesis,
            conversationHistory,
            userPrompt: promptMessage.content,
            userPromptMessageId: promptMessage.id,
            reasoningEffort,
            webEnabled,
            selfEvalEnabled,
            txtEnabled,
            plusHumanEnabled,
            prioritize,
            gradingPromptVersion: 'v1',
          });
          
          const resultData = result.data as any;
          
          if (resultData?.retryable === true) {
            console.log('⏳ Phase 1 still in progress for', promptMessage.id);
            synthesisTriggeredRef.current.delete(key);
            return;
          }
          
          console.log('✅ Synthesis triggered for prompt:', promptMessage.id, result.data);
        } catch (error) {
          console.error('❌ Failed to trigger synthesis for:', promptMessage.id, error);
          synthesisTriggeredRef.current.delete(key);
        } finally {
          synthesisInProgressRef.current.delete(key);
          console.log('🏁 Synthesis call completed for', key);
        }
      };
      
      // Fire async - don't await (let each prompt process independently)
      triggerForPrompt(targetUserMessage, synthesisKey);
    }
  }, [pendingPromptsForSynthesis, currentConversationId, localGradeState, user, messages]);

  // Create a new conversation when user sends their first message
  const createNewConversation = useCallback(async (
    initialTargetCount?: number,
    initialReasoningEffort?: 'default' | 'low' | 'medium' | 'high',
    initialPrioritize?: 'free' | 'speed' | 'balance' | 'value' | 'intelligence' | 'custom',
    initialSelectedModels?: string[],
    batchRunID?: string,
    hmaicbTimestamp?: string,
    published?: boolean
  ): Promise<string> => {
    if (!user) {
      throw new Error('User must be authenticated to create a conversation');
    }

    try {
      setLoading(true);
      const conversationId = await ConversationService.createConversation(
        user.uid,
        undefined,
        initialTargetCount,
        initialReasoningEffort,
        initialPrioritize,
        initialSelectedModels,
        batchRunID,
        hmaicbTimestamp,
        published
      );
      setCurrentConversationId(conversationId);
      return conversationId;
    } catch (error) {
      console.error('Failed to create conversation:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Add a message to the current conversation
  const addMessage = useCallback(async (
    content: string, 
    role: 'user' | 'assistant' = 'user',
    initialTargetCount?: number,
    initialReasoningEffort?: 'default' | 'low' | 'medium' | 'high',
    initialPrioritize?: 'free' | 'speed' | 'balance' | 'value' | 'intelligence' | 'custom',
    initialSelectedModels?: string[],
    webEnabled?: boolean,
    selfEvalEnabled?: boolean,
    txtEnabled?: boolean,
    sendConfig?: any,
    plusHumanContent?: string,
    batchRunID?: string,
    hmaicbTimestamp?: string,
    colorAssignments?: Map<string, string>,
    showModelNames?: boolean,
    chipAssignments?: Array<{chipId: number; color: string; openrouterId: string; displayedModelID: string}>
  ): Promise<{ messageId: string; conversationId: string } | null> => {
    if (!user) {
      throw new Error('User must be authenticated to add messages');
    }

    try {
      setLoading(true);
      
      // Use the ref to get the latest conversation ID (avoid closure issues)
      let conversationId = conversationIdRef.current;
      let isFirstMessage = false;
      
      if (!conversationId) {
        // console.log('No current conversation, creating new one with params:', {
        //   targetCount: initialTargetCount,
        //   reasoningEffort: initialReasoningEffort,
        //   prioritize: initialPrioritize,
        //   selectedModels: initialSelectedModels
        // });
        conversationId = await createNewConversation(
          initialTargetCount,
          initialReasoningEffort,
          initialPrioritize,
          initialSelectedModels,
          batchRunID,
          hmaicbTimestamp
        );
        isFirstMessage = true;
        // Small delay to ensure listener is set up
        await new Promise(resolve => setTimeout(resolve, 100));
      } else {
        // console.log('Using existing conversation:', conversationId);
        // Check if this is the first user message
        const currentMessages = messages;
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        isFirstMessage = currentMessages.length === 0 && role === 'user';
      }

      // console.log('Adding message to conversation:', conversationId, 'Role:', role, 'Content:', content);

      // Add the message with parameter settings
      const messageToSave: any = {
        role,
        content,
        webEnabled,
        selfEvalEnabled,
        txtEnabled,
        prioritizeSetting: initialPrioritize,
        aisSetting: initialTargetCount,
        reasoningSetting: initialReasoningEffort,
        sendConfig,
        plusHumanContent
      };
      
      // For user messages, freeze the chip assignments, color assignments, and display mode at time of prompt creation
      // This ensures dots and message titles maintain their visual state throughout conversation history
      if (role === 'user') {
        if (chipAssignments) {
          // NEW: Store immutable chip-ID based assignments
          // This is the canonical source of truth for color/model pairing
          messageToSave.chipAssignments = chipAssignments;
        }
        if (colorAssignments) {
          // LEGACY: Keep for backward compatibility with existing code during transition
          // Convert Map to plain object for Firestore storage
          messageToSave.colorAssignments = Object.fromEntries(colorAssignments);
        }
        if (showModelNames !== undefined) {
          // Store whether chips should show full model names (true) or creator names (false)
          messageToSave.showModelNames = showModelNames;
        }
        console.log('🎨 Freezing visual state for user prompt:', {
          chipAssignments: messageToSave.chipAssignments,
          colorAssignments: messageToSave.colorAssignments,
          showModelNames: messageToSave.showModelNames,
        });
      }
      
      console.log('💾 Saving message to Firestore:', {
        hasPlusHumanContent: !!plusHumanContent,
        plusHumanContentLength: plusHumanContent?.length,
        sendConfig: sendConfig,
        plusHumanEnabled: sendConfig?.plusHumanEnabled,
        hasColorAssignments: !!messageToSave.colorAssignments
      });
      
      const messageId = await ConversationService.addMessage(conversationId, messageToSave);

      // Note: Title generation is handled by the backend Firebase function
      // after the first user message is received. The backend will use
      // Gemini to generate a proper title based on the conversation context.

      // Update conversation timestamp AND priority setting (to match current message)
      await ConversationService.updateConversationTimestamp(conversationId);
      
      // CRITICAL: Update conversation's priority to match the current message
      // This ensures the conversation document stays in sync with its messages/grades
      if (initialPrioritize) {
        await updateDoc(doc(db, 'conversations', conversationId), {
          'modelParameters.prioritize': initialPrioritize
        });
      }

      if (isFirstMessage && role === 'user') {
        setPendingConversationId(null);
      }
      
      return { messageId, conversationId };
    } catch (error) {
      console.error('Failed to add message:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [user, createNewConversation, messages]);

  // Start a new conversation
  const startNewConversation = useCallback(async (
    initialTargetCount?: number,
    initialReasoningEffort?: 'default' | 'low' | 'medium' | 'high',
    initialPrioritize?: 'free' | 'speed' | 'balance' | 'value' | 'intelligence' | 'custom',
    initialSelectedModels?: string[],
    batchRunID?: string,
    hmaicbTimestamp?: string,
    published?: boolean
  ): Promise<string> => {
    if (!user) {
      throw new Error('User must be authenticated to start a new conversation');
    }

    console.log('🆕 Starting new conversation...');
    if (batchRunID) {
      console.log(`   🔑 Batch Run ID: ${batchRunID}`);
    }
    if (hmaicbTimestamp) {
      console.log(`   📅 HMAICB Timestamp: ${hmaicbTimestamp}`);
    }
    if (published) {
      console.log(`   📢 Published: ${published}`);
    }

    try {
      setLoading(true);
      isCreatingNewConversation.current = true;
      
      const conversationId = await ConversationService.createConversation(
        user.uid,
        undefined,
        initialTargetCount,
        initialReasoningEffort,
        initialPrioritize,
        initialSelectedModels,
        batchRunID,
        hmaicbTimestamp,
        published
      );
      
      console.log('✅ New conversation created:', conversationId);
      
      // Clear messages first, then set new conversation ID
      setMessages([]);
      messagesRef.current = [];
      
      // CRITICAL FIX: Initialize empty grade state (not null) to prevent race condition
      // When grades arrive before the async initial load completes, they need a valid state to add to
      const emptyState = createEmptyGradeState(conversationId);
      setLocalGradeState(emptyState);
      localGradeStateRef.current = emptyState;
      
      // Clear grade tracking refs for the new conversation
      processedGradeIdsRef.current.clear();
      gradesWrittenRef.current.clear();
      
      setCurrentConversationId(conversationId);
      setPendingConversationId(conversationId);
      
      // Clear the flag after a short delay to allow subscription to settle
      setTimeout(() => {
        isCreatingNewConversation.current = false;
        console.log('🏁 New conversation creation complete');
      }, 500);
      
      return conversationId;
    } catch (error) {
      console.error('❌ Failed to start new conversation:', error);
      isCreatingNewConversation.current = false;
      throw error;
    } finally {
      setLoading(false);
    }
  }, [user, currentConversationId, messages, createEmptyGradeState])

  // Switch to a different conversation
  const switchToConversation = useCallback((conversationId: string) => {
    console.log('🔄 Switching to conversation:', conversationId);
    isCreatingNewConversation.current = false; // Clear flag when switching
    
    // Clear gradesWritten ref to allow fresh writes for the new conversation
    // (Though keys include conversationId, this ensures clean state)
    gradesWrittenRef.current.clear();
    
    // CRITICAL: Clear processed grade IDs to allow fresh grade processing for new conversation
    processedGradeIdsRef.current.clear();
    
    console.log('🧹 Cleared gradesWrittenRef and processedGradeIdsRef on conversation switch');
    
    // Initialize empty grade state for the new conversation
    const emptyState = createEmptyGradeState(conversationId);
    setLocalGradeState(emptyState);
    localGradeStateRef.current = emptyState;
    console.log('🆕 Initialized empty grade state for conversation:', conversationId.substring(0, 8));
    
    setCurrentConversationId(conversationId);
  }, [createEmptyGradeState]);

  // Add a local message (for matrix messages generated on frontend)
  // Saves to Firestore so it persists across page reloads
  const addLocalMessage = useCallback(async (message: Message & { conversationId?: string }) => {
    // Extract conversationId from message if available, otherwise use currentConversationId
    const conversationId = (message as any).conversationId || currentConversationId;
    
    console.log('📥 addLocalMessage called:', {
      messageId: message.id,
      role: message.role,
      isGradeMatrix: message.isGradeMatrix,
      contentPreview: message.content.substring(0, 50),
      currentMessagesCount: messages.length,
      messageTimestamp: message.timestamp,
      conversationIdFromMessage: (message as any).conversationId,
      currentConversationId,
      finalConversationId: conversationId
    });
    
    // Check if a duplicate message already exists in current messages
    // This prevents writing duplicates if the same message was already added
    const timestamp = message.timestamp?.getTime() || 0;
    const timestampRounded = Math.floor(timestamp / 1000);
    
    let duplicateKey: string;
    if (message.isGradeMatrix) {
      const matrixType = message.content.includes('Self-Eval') ? 'selfeval' : 'peer';
      const contentHash = message.content.substring(0, 150);
      duplicateKey = `matrix_${matrixType}_${contentHash}`;
    } else {
      // For other messages, use role + timestamp + content
      duplicateKey = `${message.role}_${timestampRounded}_${message.content.substring(0, 100)}`;
    }
    
    const existingMessage = messages.find(msg => {
      const msgTimestamp = msg.timestamp?.getTime() || 0;
      const msgTimestampRounded = Math.floor(msgTimestamp / 1000);
      
      if (msg.isGradeMatrix === message.isGradeMatrix) {
        if (message.isGradeMatrix) {
          const msgMatrixType = msg.content.includes('Self-Eval') ? 'selfeval' : 'peer';
          const msgContentHash = msg.content.substring(0, 150);
          const msgKey = `matrix_${msgMatrixType}_${msgContentHash}`;
          return msgKey === duplicateKey;
        }
      }
      
      // For non-matrix messages, check role + timestamp + content
      const msgKey = `${msg.role}_${msgTimestampRounded}_${msg.content.substring(0, 100)}`;
      return msgKey === duplicateKey && Math.abs(msgTimestampRounded - timestampRounded) <= 1;
    });
    
    if (existingMessage) {
      console.warn('⚠️ Duplicate message detected in addLocalMessage, skipping write:', {
        existingMessageId: existingMessage.id,
        attemptedMessageId: message.id,
        role: message.role,
        isGradeMatrix: message.isGradeMatrix,
        contentPreview: message.content.substring(0, 50)
      });
      return; // Don't write duplicate
    }
    
    // OPTIMISTIC UPDATE: Add message to local state immediately for instant UI update
    // When Firestore sends it back via listener, duplicate detection will prevent re-adding
    setMessages(prevMessages => {
      // Insert in chronological order by timestamp
      const newMessages = [...prevMessages, message];
      newMessages.sort((a, b) => {
        const timeA = a.timestamp?.getTime() || 0;
        const timeB = b.timestamp?.getTime() || 0;
        return timeA - timeB;
      });
      return newMessages;
    });
    console.log('✅ Matrix message added to local state (optimistic)', {
      messageId: message.id,
      timestamp: message.timestamp
    });
    
    // Save to Firestore if we have a conversation ID
    if (conversationId) {
      try {
        // Remove the id and conversationId fields as Firestore will generate id, but keep timestamp
        const { id, conversationId: _, ...messageWithoutId } = message as any;
        await ConversationService.addMessage(conversationId, messageWithoutId);
        console.log('✅ Matrix message saved to Firestore', {
          messageId: message.id,
          conversationId: conversationId.substring(0, 20),
          timestamp: message.timestamp,
          usedMessageConvId: !!(message as any).conversationId,
          usedHookConvId: !currentConversationId
        });
      } catch (error) {
        console.error('❌ Failed to save matrix message to Firestore:', error, {
          messageId: message.id,
          attemptedConvId: conversationId?.substring(0, 20)
        });
      }
    } else {
      console.warn('⚠️ Cannot persist matrix message: no conversationId available', {
        messageId: message.id,
        hadMessageConvId: !!(message as any).conversationId,
        hadHookConvId: !!currentConversationId
      });
    }
    
    // Note: We don't need to update local state here because the Firestore listener
    // will automatically pick up the new message and add it to the messages array
  }, [currentConversationId, messages]);

  // +Human: Delete a star rating grade from a message
  const deleteStarGrade = useCallback(async (messageId: string) => {
    console.log('🗑️ deleteStarGrade called:', { messageId, hasUser: !!user, hasConversationId: !!currentConversationId });
    
    if (!user || !currentConversationId) {
      console.error('❌ Cannot delete star grade: missing user or conversationId');
      return;
    }
    
    try {
      // Delete the humanGrade field from the message document
      const messageRef = doc(db, 'conversations', currentConversationId, 'messages', messageId);
      await updateDoc(messageRef, {
        humanGrade: null
      });
      console.log('✅ Human grade deleted from message document');
      
      // Note: We're not deleting from the grades collection because that's for analytics
      // The grade will just not show up in the UI anymore
    } catch (error) {
      console.error('❌ Failed to delete star grade:', error);
    }
  }, [user, currentConversationId]);

  // +Human: Assign a star rating grade to a message
  const assignStarGrade = useCallback(async (
    messageId: string,
    grade: 'A' | 'B' | 'C' | 'D' | 'F',
    candidateModelId: string,
    isSelfEval: boolean
  ) => {
    console.log('⭐ assignStarGrade called:', { messageId, grade, candidateModelId, isSelfEval, hasUser: !!user, hasConversationId: !!currentConversationId });
    
    if (!user || !currentConversationId) {
      console.error('❌ Cannot assign star grade: missing user or conversationId', { hasUser: !!user, hasConversationId: !!currentConversationId });
      return;
    }
    
    console.log('⭐ Assigning star grade:', { messageId, grade, candidateModelId, isSelfEval });
    
    // Find the message to get context
    const message = messages.find(m => m.id === messageId);
    if (!message) {
      console.error('Message not found:', messageId);
      return;
    }
    
    // Find the user prompt message
    const userPromptMessage = messages.slice().reverse().find(m => m.role === 'user');
    if (!userPromptMessage) {
      console.error('No user prompt found');
      return;
    }
    
    // Determine if TxT mode is active
    const txtModeActive = userPromptMessage.sendConfig?.txt || false;
    const plusHumanOn = userPromptMessage.sendConfig?.plusHumanEnabled || false;
    
    // Build grade document
    const gradeData: any = {
      grade,
      timestamp: serverTimestamp(),
      conversationId: currentConversationId,
      userId: user.uid,
      
      // Candidate being graded
      candidateModelId,
      creator: candidateModelId.split('/')[0] || candidateModelId,
      modelId: candidateModelId,
      
      // Human grader identification
      graderModelId: user.uid,
      isHumanGrader: true,
      humanGraderId: user.uid,
      
      // Context
      plusHumanOn,
      txtModeActive,
      isSelfEval,
      
      // Standard fields
      active: true,
      prioritySetting: userPromptMessage.sendConfig?.prioritize || 'speed',
      aisNumber: userPromptMessage.sendConfig?.ais || 3,
      reasoningSetting: userPromptMessage.sendConfig?.reasoning || 'default',
      userPromptMessageId: userPromptMessage.id,
      webGroundingSetting: userPromptMessage.sendConfig?.webGrounding || false,
      selfevalPrehocSetting: userPromptMessage.sendConfig?.selfeval || false,
    };
    
    // Write to Firestore immediately
    try {
      await addDoc(collection(db, 'grades'), gradeData);
      console.log('✅ Star grade written to Firestore');
      
      // Update the message document with the human grade for persistence
      const messageRef = doc(db, 'conversations', currentConversationId, 'messages', messageId);
      await updateDoc(messageRef, {
        humanGrade: {
          grade,
          assignedAt: serverTimestamp(),
          assignedBy: user.uid,
          txtModeActive
        }
      });
      console.log('✅ Human grade saved to message document');
      
      // Update TxT pause counters (local state + Firestore)
      if (txtModeActive && plusHumanOn) {
        const conversationRef = doc(db, 'conversations', currentConversationId);
        
        if (isSelfEval) {
          // Ionoff grade
          setTxtIonoffGradeReceived(prev => prev + 1);
          await setDoc(conversationRef, {
            txtIonoffGradeReceived: increment(1)
          }, { merge: true });
          console.log('✅ Incremented txtIonoffGradeReceived in Firestore');
        } else {
          // Model grade
          setTxtModelGradesReceived(prev => prev + 1);
          await setDoc(conversationRef, {
            txtModelGradesReceived: increment(1)
          }, { merge: true });
          console.log('✅ Incremented txtModelGradesReceived in Firestore');
        }
      }
    } catch (error) {
      console.error('❌ Failed to write star grade:', error);
    }
  }, [user, currentConversationId, messages, txtModelGradesReceived, txtIonoffGradeReceived]);

  // +Human: Resume from TxT pause
  const resumeFromPause = useCallback(async () => {
    if (!currentConversationId || !txtPaused || !user) return;
    
    // 🛡️ Prevent duplicate resume calls
    if (isResuming) {
      console.log('⏭️ Already resuming, skipping duplicate call');
      return;
    }
    
    console.log('▶️ Resuming from pause, phase:', txtPausePhase);
    
    const phaseToResume = txtPausePhase; // Capture before clearing
    
    // Set resuming state to prevent double-clicks
    setIsResuming(true);
    
    try {
      // Clear pause state in Firestore
      const conversationRef = doc(db, 'conversations', currentConversationId);
      await setDoc(conversationRef, {
        txtPaused: false,
        txtPausePhase: null,
        txtModelGradesReceived: 0,
        txtIonoffGradeReceived: 0,
        txtModelResumeMessageId: null,
        txtIonoffResumeMessageId: null,
      }, { merge: true });
      
      console.log('✅ Cleared TxT pause state in Firestore');
      
      // Update local state
      setTxtPaused(false);
      setTxtPausePhase(null);
      setTxtModelGradesReceived(0);
      setTxtIonoffGradeReceived(0);
      setTxtResumeMessageId(null);
      
      if (phaseToResume === 'awaiting-model-grades') {
        console.log('📍 Phase 1 → Phase 2: Triggering synthesis');
        // Synthesis will be triggered by the existing useEffect when
        // allPeerGradesReceived becomes true
      } else if (phaseToResume === 'awaiting-ionoff-grade') {
        console.log('📍 Phase 2 → Phase 3: Triggering self-eval');
        
        // Call the NEW triggerSelfEval function
        const functions = getFunctions();
        const triggerSelfEvalFn = httpsCallable(functions, 'triggerSelfEval');
        
        // Get the last user message to extract config
        const lastUserMessage = messages
          .filter(m => m.role === 'user')
          .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())[0];
        
        if (!lastUserMessage) {
          console.error('❌ No user message found for self-eval trigger');
          return;
        }
        
        // Get ALL responses (INCLUDING human response for self-eval)
        // Human response MUST be included in self-eval in both TxT and non-TxT modes
        const lastUserMessageTime = lastUserMessage.timestamp;
        const aiResponses = messages
          .filter(m => {
            if (m.role !== 'assistant') return false;
            if (!m.modelUsed) return false;
            
            // 🎯 CRITICAL: Check userPromptMessageId if available (parallel prompt safety)
            if (m.userPromptMessageId) {
              if (m.userPromptMessageId !== lastUserMessage.id) return false;
            }
            
            // Timestamp sanity check (always apply)
            if (m.timestamp < lastUserMessageTime) return false;
            
            // DO NOT exclude human response - it must be included in self-eval
            // if (m.isHumanResponse) return false; // ❌ REMOVED - human must be in self-eval
            if (m.isSynthesized) return false;
            if (m.synthesisVariant) return false;
            if (m.isGradingResponse) return false;
            if (m.isExtractionResponse) return false;
            if (m.isCostSummary) return false;
            if (m.isGradeMatrix) return false;
            if (m.isDebugInfo) return false;
            if ((m as any).isShannonDebug) return false;
            if ((m as any).isPromptAnalysisDebug) return false;
            return true;
          })
          .map(m => ({
            modelId: m.actualModelId || m.modelUsed || '',
            content: m.content,
            displayedModelId: m.modelUsed || '',
            isHumanResponse: m.isHumanResponse || false,
          }));
        
        console.log('📦 Self-eval responses (includes human):', {
          totalResponses: aiResponses.length,
          includingHuman: aiResponses.some(r => r.isHumanResponse),
          responseModelIds: aiResponses.map(r => r.modelId)
        });
        
        // Find the ionoff message
        const ionoffMessage = messages
          .filter(m => m.role === 'assistant' && m.isSynthesized && !m.synthesisVariant)
          .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())[0];
        
        if (!ionoffMessage) {
          console.error('❌ No ionoff message found for self-eval trigger');
          return;
        }
        
        const conversationHistory: any[] = [];
        const selfEvalEnabled = lastUserMessage.sendConfig?.selfeval ?? false;
        const webEnabled = lastUserMessage.sendConfig?.webGrounding ?? true;
        const reasoningEffort = lastUserMessage.sendConfig?.reasoning || 'default';
        const plusHumanEnabled = lastUserMessage.sendConfig?.plusHumanEnabled ?? false;
        const prioritySetting = lastUserMessage.sendConfig?.prioritize || 'speed';
        
        console.log('📦 Triggering self-eval (Phase 3):', {
          conversationId: currentConversationId,
          userId: user.uid,
          ionoffMessageId: ionoffMessage.id,
          responseCount: aiResponses.length,
          plusHumanEnabled,
        });
        
        await triggerSelfEvalFn({
          conversationId: currentConversationId,
          userId: user.uid,
          ionoffMessageId: ionoffMessage.id,
          aiResponses,
          conversationHistory,
          userPrompt: lastUserMessage.content,
          userPromptMessageId: lastUserMessage.id,
          reasoningEffort,
          webEnabled,
          gradingPromptVersion: 'v1',
          synthesisPromptVersion: ionoffMessage.ionoffVersion?.split('-')[2] || 'v1',
          synthesisPromptVariant: ionoffMessage.ionoffVersion?.split('-').slice(3).join('-') || 'new-plain',
          ionoffVersion: ionoffMessage.ionoffVersion || 'ionoff-grading-v1-synthesis-v1-new-plain',
          prioritySetting,
          plusHumanEnabled,
        });
        
        console.log('✅ Self-eval triggered after resume (Phase 3)');
      }
    } catch (error) {
      console.error('❌ Failed to resume from pause:', error);
    } finally {
      // Always clear resuming state
      setIsResuming(false);
    }
  }, [currentConversationId, txtPaused, txtPausePhase, user, messages, isResuming]);

  // +Human: Check if resume button should be enabled
  const canResume = useMemo(() => {
    if (!txtPaused) return false;
    
    if (txtPausePhase === 'awaiting-model-grades') {
      return txtModelGradesReceived >= 2; // Minimum 2 model grades
    } else if (txtPausePhase === 'awaiting-ionoff-grade') {
      return txtIonoffGradeReceived >= 1; // Minimum 1 ionoff grade
    }
    
    return false;
  }, [txtPaused, txtPausePhase, txtModelGradesReceived, txtIonoffGradeReceived]);

  // 🎯 PHASE 1: Mark EOP (End of Processing) on user prompt messages
  // Track which prompts we've already marked to avoid redundant Firestore writes
  const eopMarkedRef = useRef<Set<string>>(new Set());
  
  useEffect(() => {
    if (!currentConversationId || messages.length === 0) {
      return;
    }

    // Get all user prompts that haven't been marked with EOP yet
    const userPrompts = messages
      .filter(m => m.role === 'user' && !m.overwritten && !m.eop && !m.stoppedBeforeEOP)
      .sort((a, b) => (a.timestamp?.getTime() || 0) - (b.timestamp?.getTime() || 0));

    for (const prompt of userPrompts) {
      // Skip if we've already processed this prompt
      if (eopMarkedRef.current.has(prompt.id)) {
        continue;
      }

      const promptTime = prompt.timestamp?.getTime() || 0;
      const targetCount = prompt.sendConfig?.ais || 3;
      const selfEvalEnabled = prompt.sendConfig?.selfeval ?? false;

      // Get model responses for this prompt (exclude special messages)
      const modelResponses = messages.filter(m => {
        if (m.role !== 'assistant') return false;
        if (!m.modelUsed) return false;
        
        // 🎯 CRITICAL: Check userPromptMessageId if available (parallel prompt safety)
        // If the field exists, it must match. If missing (legacy), fall back to timestamp.
        if (m.userPromptMessageId) {
          if (m.userPromptMessageId !== prompt.id) return false;
        }
        
        // Timestamp sanity check (always apply)
        if ((m.timestamp?.getTime() || 0) < promptTime) return false;
        
        // Exclude special message types
        if (m.isSynthesized) return false;
        if (m.synthesisVariant) return false;
        if (m.isGradingResponse) return false;
        if (m.isExtractionResponse) return false;
        if (m.isCostSummary) return false;
        if (m.isGradeMatrix) return false;
        if (m.isDebugInfo) return false;
        if ((m as any).isShannonDebug) return false;
        if ((m as any).isPromptAnalysisDebug) return false;
        
        return true;
      });

      // Get synthesis message for this prompt
      const synthesisMessage = messages.find(m => {
        if (!m.isSynthesized) return false;
        if (m.synthesisVariant) return false;
        if (m.role !== 'assistant') return false;
        
        // 🎯 CRITICAL: Check userPromptMessageId if available
        if (m.userPromptMessageId) {
          if (m.userPromptMessageId !== prompt.id) return false;
        }
        
        // Timestamp sanity check
        if ((m.timestamp?.getTime() || 0) <= promptTime) return false;
        
        return true;
      });

      // Get self-eval grade matrix for this prompt
      // 🎯 The matrix (generated by frontend GradeMatrixGenerator) is the EOP signal
      const selfEvalMatrix = messages.find(m => {
        if (!m.isGradeMatrix) return false;
        if (!m.content?.includes('Self-Eval')) return false;
        
        // 🎯 CRITICAL: Check userPromptMessageId if available
        if (m.userPromptMessageId) {
          if (m.userPromptMessageId !== prompt.id) return false;
        }
        
        // Timestamp sanity check
        if ((m.timestamp?.getTime() || 0) <= promptTime) return false;
        
        return true;
      });

      let shouldMarkEOP = false;
      let eopReason = '';

      // CASE 1: T=1 (single model, no synthesis)
      if (targetCount === 1 && modelResponses.length >= 1) {
        shouldMarkEOP = true;
        eopReason = 'T=1 (single model response received)';
      }
      // CASE 2: Self-eval enabled - wait for self-eval matrix
      else if (selfEvalEnabled && selfEvalMatrix) {
        shouldMarkEOP = true;
        eopReason = 'Self-eval matrix completed';
      }
      // CASE 3: Self-eval disabled - wait for synthesis/ionoff response
      else if (!selfEvalEnabled && synthesisMessage) {
        shouldMarkEOP = true;
        eopReason = 'Synthesis/ionoff response received';
      }

      if (shouldMarkEOP) {
        // Mark this prompt as processed to avoid redundant writes
        eopMarkedRef.current.add(prompt.id);

        // Update Firestore
        (async () => {
          try {
            const promptRef = doc(
              db,
              'conversations',
              currentConversationId,
              'messages',
              prompt.id
            );
            await updateDoc(promptRef, {
              eop: true,
              eopReachedAt: serverTimestamp(),
            });
            console.log(`✅ Marked EOP for prompt ${prompt.id}: ${eopReason}`);
          } catch (error) {
            console.error(`❌ Failed to mark EOP for prompt ${prompt.id}:`, error);
            // Remove from processed set so we can retry
            eopMarkedRef.current.delete(prompt.id);
          }
        })();
      }
    }
  }, [currentConversationId, messages]);

  // 🎯 PHASE 1: Mark firstResponseReceivedAt on user prompt messages
  // Track which prompts we've already marked to avoid redundant Firestore writes
  const firstResponseMarkedRef = useRef<Set<string>>(new Set());
  
  useEffect(() => {
    if (!currentConversationId || messages.length === 0) {
      return;
    }

    // Get all user prompts that haven't received their first response yet
    const userPrompts = messages
      .filter(m => m.role === 'user' && !m.overwritten && !m.firstResponseReceivedAt)
      .sort((a, b) => (a.timestamp?.getTime() || 0) - (b.timestamp?.getTime() || 0));

    for (const prompt of userPrompts) {
      // Skip if we've already processed this prompt
      if (firstResponseMarkedRef.current.has(prompt.id)) {
        continue;
      }

      const promptTime = prompt.timestamp?.getTime() || 0;

      // Get model responses for this prompt (exclude special messages)
      const modelResponses = messages.filter(m => {
        if (m.role !== 'assistant') return false;
        if (!m.modelUsed) return false;
        
        // 🎯 CRITICAL: Check userPromptMessageId if available (parallel prompt safety)
        // If the field exists, it must match. If missing (legacy), fall back to timestamp.
        if (m.userPromptMessageId) {
          if (m.userPromptMessageId !== prompt.id) return false;
        }
        
        // Timestamp sanity check (always apply)
        if ((m.timestamp?.getTime() || 0) <= promptTime) return false;
        
        // Exclude special message types
        if (m.isSynthesized) return false;
        if (m.synthesisVariant) return false;
        if (m.isGradingResponse) return false;
        if (m.isExtractionResponse) return false;
        if (m.isCostSummary) return false;
        if (m.isGradeMatrix) return false;
        if (m.isDebugInfo) return false;
        if ((m as any).isShannonDebug) return false;
        if ((m as any).isPromptAnalysisDebug) return false;
        
        return true;
      });

      // If at least one model response has arrived, mark firstResponseReceivedAt
      if (modelResponses.length > 0) {
        // Mark this prompt as processed to avoid redundant writes
        firstResponseMarkedRef.current.add(prompt.id);

        // Update Firestore
        (async () => {
          try {
            const promptRef = doc(
              db,
              'conversations',
              currentConversationId,
              'messages',
              prompt.id
            );
            await updateDoc(promptRef, {
              firstResponseReceivedAt: serverTimestamp(),
            });
            console.log(`✅ Marked firstResponseReceivedAt for prompt ${prompt.id} (${modelResponses.length} responses received)`);
          } catch (error) {
            console.error(`❌ Failed to mark firstResponseReceivedAt for prompt ${prompt.id}:`, error);
            // Remove from processed set so we can retry
            firstResponseMarkedRef.current.delete(prompt.id);
          }
        })();
      }
    }
  }, [currentConversationId, messages]);

  // Admin: Add conversations from external source (e.g., from all users)
  const addExternalConversations = useCallback((externalConversations: Conversation[]) => {
    setConversations((prevConversations) => {
      // Merge with existing conversations (no duplicates)
      const existingIds = new Set(prevConversations.map(c => c.id));
      const newConversations = externalConversations.filter(c => !existingIds.has(c.id));
      
      if (newConversations.length === 0) {
        console.log('ℹ️ No new conversations to add (all already in history)');
        return prevConversations;
      }
      
      console.log(`📝 Adding ${newConversations.length} external conversations to history`);
      
      // Combine and sort by lastUpdatedAt (most recent first)
      const combined = [...prevConversations, ...newConversations];
      combined.sort((a, b) => b.lastUpdatedAt.getTime() - a.lastUpdatedAt.getTime());
      
      return combined;
    });
  }, []);

  // Published conversations feature
  const publishCurrentConversation = useCallback(async (conversationId?: string) => {
    const targetId = conversationId || currentConversationId;
    if (!targetId) return;
    
    try {
      await ConversationService.publishConversation(targetId);
      console.log('✅ Conversation published:', targetId);
      
      // Update local state to reflect published status
      setConversations(prevConversations =>
        prevConversations.map(conv =>
          conv.id === targetId
            ? { ...conv, published: true }
            : conv
        )
      );
    } catch (error) {
      console.error('❌ Error publishing conversation:', error);
      throw error;
    }
  }, [currentConversationId]);

  const unpublishCurrentConversation = useCallback(async (conversationId?: string) => {
    const targetId = conversationId || currentConversationId;
    if (!targetId) return;
    
    try {
      await ConversationService.unpublishConversation(targetId);
      console.log('✅ Conversation unpublished:', targetId);
      
      // Update local state to reflect unpublished status
      setConversations(prevConversations =>
        prevConversations.map(conv =>
          conv.id === targetId
            ? { ...conv, published: false }
            : conv
        )
      );
    } catch (error) {
      console.error('❌ Error unpublishing conversation:', error);
      throw error;
    }
  }, [currentConversationId]);

  const forkAndSendMessage = useCallback(async (
    sourceConversationId: string,
    messageContent: string,
    messageOptions?: any
  ): Promise<string> => {
    if (!user) throw new Error('User not authenticated');
    
    try {
      // 1. Fork the conversation
      console.log('🍴 Forking conversation:', sourceConversationId);
      const newConversationId = await ConversationService.forkConversation(
        sourceConversationId,
        user.uid
      );
      
      console.log('✅ Conversation forked:', newConversationId);
      
      // 2. Set as current conversation (critical for message to go to right place)
      setCurrentConversationId(newConversationId);
      conversationIdRef.current = newConversationId;
      
      // 3. Add the message to the NEW conversation
      await addMessage(messageContent, 'user');
      
      console.log('✅ Message sent to forked conversation');
      return newConversationId;
    } catch (error) {
      console.error('❌ Error forking conversation:', error);
      throw error;
    }
  }, [user, setCurrentConversationId, addMessage]);

  // Right to Privacy: Replace all user content with "right2Bforgotten"
  const right2Privacy = useCallback(async () => {
    if (!user) throw new Error('User not authenticated');
    
    try {
      console.log('🔒 Initiating right2Bforgotten for user:', user.uid);
      const result = await ConversationService.right2Privacy(user.uid);
      
      // Update local state to reflect the changes
      setConversations(prevConversations =>
        prevConversations.map(conv => ({
          ...conv,
          title: 'right2Bforgotten',
          published: false
        }))
      );
      
      // Clear messages for current conversation (they've been replaced in Firestore)
      // The subscription will re-fetch and show the updated content
      setMessages([]);
      
      console.log(`✅ Right2Privacy completed: ${result.conversationsUpdated} conversations, ${result.messagesUpdated} messages`);
      return result;
    } catch (error) {
      console.error('❌ Error in right2Bforgotten:', error);
      throw error;
    }
  }, [user]);

  return {
    currentConversationId,
    messages,
    conversations,
    publishedConversations,
    loading,
    isProcessing,
    addMessage,
    startNewConversation,
    setCurrentConversationId,
    switchToConversation,
    addLocalMessage, // NEW: For adding frontend-generated messages
    // Local grade processing
    localGradeState,
    gradeProcessor, // NEW: Expose processor for matrix generation
    pendingConversationId,
    // Admin: Add external conversations
    addExternalConversations,
    // +Human feature
    assignStarGrade,
    deleteStarGrade, // NEW: Delete star rating
    resumeFromPause,
    canResume,
    isResuming, // NEW: Prevent duplicate resume calls
    txtPaused,
    txtPausePhase,
    txtModelGradesReceived,
    txtIonoffGradeReceived,
    txtResumeMessageId,
    // Published conversations feature
    publishCurrentConversation,
    unpublishCurrentConversation,
    forkAndSendMessage,
    // Right to Privacy
    right2Privacy,
  };
};
